import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const GradientText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  letterSpacing: '-0.02em',
  textShadow: '0 0 30px rgba(102, 126, 234, 0.3)',
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: 'rgba(20, 20, 20, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(2),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.2)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1.5),
  fontWeight: 500,
  fontSize: '1rem',
  textTransform: 'none',
  background: 'linear-gradient(45deg, #667eea, #764ba2)',
  '&:hover': {
    background: 'linear-gradient(45deg, #5a71d5, #6a4292)',
    boxShadow: '0 5px 15px rgba(102, 126, 234, 0.4)',
  },
  marginTop: theme.spacing(2),
}));

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      py: 2,
      height: '100%',
      justifyContent: 'flex-start'
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            textAlign: 'center', 
            pt: { xs: 2, md: 4 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <GradientText
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              mb: 2,
            }}
          >
            Plex
          </GradientText>
          
          <Box
            sx={{
              width: '100px',
              height: '3px',
              background: 'linear-gradient(90deg, #667eea, #764ba2)',
              margin: '0 auto',
              borderRadius: '2px',
              mb: 4,
            }}
          />
          
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 300,
              mb: 3,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Supercharge your SEO workflows with MCP-powered automation for precision insights and zero guesswork.
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 6,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Plex brings the power of Model Context Protocol (MCP) tools to your SEO stack for smarter audits and faster execution.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ActionButton 
              variant="contained" 
              size="large"
              onClick={() => navigate('/prompts')}
            >
              Explore Prompts
            </ActionButton>
          </Box>
        </Box>
      </Container>
      
      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={0}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#667eea',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  MCP Integration
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, flexGrow: 1 }}
              >
                Seamlessly connect Claude AI to your Google Ads data through Model Context Protocol for real-time insights and analysis.
              </Typography>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={0}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#764ba2',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Smart Prompts
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, flexGrow: 1 }}
              >
                Access our library of expert-crafted prompts designed to extract meaningful insights from your advertising data.
              </Typography>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FeatureCard elevation={0}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#f093fb',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Secure Authentication
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, flexGrow: 1 }}
              >
                Industry-standard OAuth 2.0 authentication ensures your Google Ads data remains secure while enabling powerful AI analysis.
              </Typography>
            </FeatureCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingPage;
